import { API_KEY_PERMISSIONS, DEFAULT_API_KEY_PERMISSIONS } from '@aline/common';

export type AccessPreset = 'custom' | 'full' | 'none' | 'read';

interface PermissionOption {
  label: string;
  value: string;
}

interface PermissionGroup {
  label: string;
  permissions: PermissionOption[];
}

export const apiKeyPermissionGroups: PermissionGroup[] = [
  {
    label: 'Account',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.ACCOUNT_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.ACCOUNT_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.ACCOUNT_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.ACCOUNT_DELETE },
    ],
  },
  {
    label: 'Documents',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.DOCUMENTS_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.DOCUMENTS_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.DOCUMENTS_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.DOCUMENTS_DELETE },
    ],
  },
  {
    label: 'Chats',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.CHATS_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.CHATS_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.CHATS_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.CHATS_DELETE },
    ],
  },
  {
    label: 'Templates',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.TEMPLATES_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.TEMPLATES_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.TEMPLATES_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.TEMPLATES_DELETE },
    ],
  },
  {
    label: 'Playbooks',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.PLAYBOOKS_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.PLAYBOOKS_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.PLAYBOOKS_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.PLAYBOOKS_DELETE },
    ],
  },
  {
    label: 'Reports',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.REPORTS_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.REPORTS_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.REPORTS_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.REPORTS_DELETE },
    ],
  },
  {
    label: 'Reminders',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.REMINDERS_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.REMINDERS_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.REMINDERS_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.REMINDERS_DELETE },
    ],
  },
  {
    label: 'Workflows',
    permissions: [
      { label: 'Read', value: API_KEY_PERMISSIONS.WORKFLOWS_READ },
      { label: 'Create', value: API_KEY_PERMISSIONS.WORKFLOWS_CREATE },
      { label: 'Update', value: API_KEY_PERMISSIONS.WORKFLOWS_UPDATE },
      { label: 'Delete', value: API_KEY_PERMISSIONS.WORKFLOWS_DELETE },
    ],
  },
];

export function getModalTitle(modalState: string) {
  switch (modalState) {
    case 'success':
      return 'API Key - Success';
    default:
      return 'API Key - Creation';
  }
}

export function getToggledPermissions(permissions: string[], permission: string) {
  if (permissions.includes(permission)) return permissions.filter(p => p !== permission);
  return [...permissions, permission];
}

export function getAllPermissions() {
  return apiKeyPermissionGroups.flatMap(group => group.permissions.map(p => p.value));
}

export function getColumnPermissions(action: string) {
  const actionLower = action.toLowerCase();
  return apiKeyPermissionGroups
    .flatMap(group => group.permissions)
    .filter(p => p.value.endsWith(`:${actionLower}`))
    .map(p => p.value);
}

export function getToggledRow(current: string[], rowPermissions: string[]) {
  const allSelected = rowPermissions.every(p => current.includes(p));
  if (allSelected) return current.filter(p => !rowPermissions.includes(p));
  return [...new Set([...current, ...rowPermissions])];
}

export function getToggledColumn(current: string[], action: string) {
  const columnPermissions = getColumnPermissions(action);
  const allSelected = columnPermissions.every(p => current.includes(p));
  if (allSelected) return current.filter(p => !columnPermissions.includes(p));
  return [...new Set([...current, ...columnPermissions])];
}

export function getActivePreset(permissions: string[]): AccessPreset {
  if (permissions.length === 0) return 'none';

  const allPermissions = getAllPermissions();
  if (
    permissions.length === allPermissions.length &&
    allPermissions.every(p => permissions.includes(p))
  ) {
    return 'full';
  }

  const readOnly = [...DEFAULT_API_KEY_PERMISSIONS];
  if (permissions.length === readOnly.length && readOnly.every(p => permissions.includes(p))) {
    return 'read';
  }

  return 'custom';
}
